/**
 * ID Field Detection Utilities
 *
 * Helper functions for detecting ID fields in database records
 * and inferring object types from field names.
 */

import { ObjectType, OBJECT_TYPES } from '@206mp/shared/utils/globalObjectId';

// ============================================================================
// Field Name Detection
// ============================================================================

/**
 * Check if a field name indicates it's an ID field
 * @param fieldName - Field name to check (e.g., "id", "user_id", "userId")
 * @returns true if field name matches ID pattern
 */
export function isIdField(fieldName: string): boolean {
  const normalizedName = fieldName.toLowerCase();
  return (
    normalizedName === 'id' ||
    normalizedName.endsWith('_id') ||
    normalizedName.endsWith('id')
  );
}

// ============================================================================
// Value Type Detection
// ============================================================================

/**
 * Check if a value looks like a database ID
 * @param value - Value to check
 * @returns true if value is a positive integer (number or numeric string)
 */
export function looksLikeDbId(value: unknown): boolean {
  // Handle numeric strings (from JSON/API responses)
  if (typeof value === 'string') {
    // Must be a numeric string
    if (!/^\d+$/.test(value)) return false;
    const num = parseInt(value, 10);
    return num > 0 && Number.isSafeInteger(num);
  }

  // Handle actual numbers
  if (typeof value !== 'number') return false;
  if (!Number.isInteger(value)) return false;
  return value > 0;
}

// ============================================================================
// Object Type Inference
// ============================================================================

/**
 * Map field names to object types
 */
const FIELD_NAME_TO_TYPE_MAP: Record<string, ObjectType> = {
  'user_id': 'users',
  'userid': 'users',
  'photo_id': 'photos',
  'photoid': 'photos',
  'collection_id': 'collections',
  'collectionid': 'collections',
  'collection_item_id': 'collection_items',
  'collectionitemid': 'collection_items',
  'critique_id': 'critiques',
  'critiqueid': 'critiques',
  'critique_edit_id': 'critique_edits',
  'critiqueeditid': 'critique_edits',
  'critique_session_id': 'critique_sessions',
  'critiquesessionid': 'critique_sessions',
};

/**
 * Infer object type from field name
 * @param fieldName - Field name (e.g., "user_id", "photoId", "collection_item_id")
 * @returns Object type or null if cannot be determined
 */
export function inferObjectTypeFromFieldName(fieldName: string): ObjectType | null {
  // Handle primary key "id" field - cannot infer type
  if (fieldName.toLowerCase() === 'id') {
    return null;
  }

  // Normalize to lowercase for lookup
  const normalized = fieldName.toLowerCase();

  // Direct lookup with lowercase
  if (normalized in FIELD_NAME_TO_TYPE_MAP) {
    return FIELD_NAME_TO_TYPE_MAP[normalized];
  }

  // Convert camelCase to snake_case for consistent lookup
  // Only do this conversion if not already snake_case
  if (!normalized.includes('_')) {
    const snakeCase = fieldName
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, ''); // Remove leading underscore if added

    if (snakeCase in FIELD_NAME_TO_TYPE_MAP) {
      return FIELD_NAME_TO_TYPE_MAP[snakeCase];
    }
  }

  // Fallback: Try to extract type hint
  // Check if the field name contains any known type pattern
  for (const [pattern, type] of Object.entries(FIELD_NAME_TO_TYPE_MAP)) {
    const basePattern = pattern.replace('_id', '');
    if (normalized.includes(basePattern)) {
      return type;
    }
  }

  // Cannot determine type
  return null;
}

/**
 * Check if a string is a valid object type
 * @param type - Type string to validate
 * @returns true if type is a known object type
 */
export function isValidObjectType(type: unknown): type is ObjectType {
  if (typeof type !== 'string') return false;
  return type in OBJECT_TYPES;
}
